
/** @jsxImportSource @emotion/react */
import { faUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useParams } from "react-router-dom";
import { validatePortableRegex } from "../../../../utils/portableRegex";
import useConfig from "../../../../actions/Tenants/config/configHook";
import FilterableConfigList from "./FilterableConfigList";
import { attributeIsSet } from "./flagConstants";
import SubconfigForm from "./SubConfigForm";

export const default_trigger_config = {
    check_locations: [],
    fruit_types: [],
    layer_types: [],
    only_trigger_for_check_type: undefined,
    meta_conditions: []
};


export default function ColorCodeTriggerForm({ object, setObject, description, attributes = ["check_locations", "fruit_types", "exclude_fruit_types", "fruit_varieties", "exclude_fruit_varieties", "layer_types", "meta_conditions"] }) {
    const config = useConfig();
    const fruitOptions = config.fruit_types.map(({ value, text }) => ({ value, label: `${text} (${value})` }));
    const layerTypeOptions = config.layer_types.map(({ value, text }) => ({ value, label: `${text} (${value})` }));
    const checkLocationOptions = config.check_locations.map(({ value, text }) => ({ value, label: `${text} (${value})` }));
    const getForm = (object) => {
        const form = [
            // {
            //     type: "header",
            //     label: "Triggers conditions",
            // },
            {
                label: "Trigger on Check Location",
                name: "check_locations",
                description: "Trigger these rules on specific check locations. Leave empty to trigger on all.",
                type: "single-select",
                options: checkLocationOptions,
                return_array: true,
                multi: true,
            },
            {
                label: "Trigger on Layer Types",
                name: "layer_types",
                description: "Trigger these rules on specific layer types. Leave empty to trigger on all.",
                type: "single-select",
                options: layerTypeOptions,
                return_array: true,
                multi: true,
            },
            {
                label: "Trigger on Fruit Types",
                name: "fruit_types",
                description: "Only trigger these rules on specific fruit types. Leave empty to trigger on all.",
                type: "single-select",
                return_array: true,
                options: fruitOptions,
                multi: true,
            },
            {
                label: "Ignore Fruit Types",
                name: "exclude_fruit_types",
                description: "Do not trigger these rules on specific fruit types. Leave empty to trigger on all.",
                type: "single-select",
                return_array: true,
                options: fruitOptions,
                multi: true,
            },
            {
                label: "Only trigger on specific check",
                name: "manual_or_with_mini",
                description: "Only trigger on specific checks. Leave empty to trigger on all.",
                options: [
                    {
                        value: "only_on_mini_check",
                        label: "Only on mini checks"
                    },
                    {
                        value: "only_on_manual_check",
                        label: "Only on manual checks"
                    },
                ],
                type: "single-select",
            },
            {
                type: "element",
                name: "meta_conditions",
                el: <MetaConditionsForm name="meta_conditions" title="Meta conditions" description="Define meta conditions to filter on" fields={object?.meta_conditions || []} setArray={(meta_conditions) => setObject({ ...object, meta_conditions })} />
            }
        ];

        return form.filter((i) => i.type === "header" || attributes.includes(i.name as any));
    };
    return <SubconfigForm
        name="trigger"
        title="Trigger"
        description={description}
        getForm={getForm}
        subconfig={object}
        setSubconfig={setObject}
        defaultSubconfig={default_trigger_config}
    />;
}


const defaultField = {
    field: {
        meta_location: "layer_meta",
        field: "sup_ref",
        options_name: "sup_ref",
        include_values: [],
        exclude_values: [],
    },
    display_filter: {
        fruit_types: [],
        exclude_fruit_types: [],
        layer_types: [],
        // exclude_layer_types: [],
    }
};

export function MetaConditionsForm({ name, title, fields, description, setArray }) {
    const { tenant_id } = useParams();
    const getFieldTitle = (object) => {
        const link = <Link to={`/tenants/${tenant_id}/form-field-options/${object.options_name}`} target="_blank" rel="noopener noreferrer" >{object.meta_location} [{object.field}]<FontAwesomeIcon icon={faUpRightFromSquare} /> </Link>;

        const conditions: string[] = [];

        // Add operator conditions
        if (attributeIsSet(object.gte)) {
            conditions.push(`>= ${object.gte}`);
        }
        if (attributeIsSet(object.gt)) {
            conditions.push(`> ${object.gt}`);
        }
        if (attributeIsSet(object.lte)) {
            conditions.push(`<= ${object.lte}`);
        }
        if (attributeIsSet(object.lt)) {
            conditions.push(`< ${object.lt}`);
        }
        if (attributeIsSet(object.eq)) {
            conditions.push(`= ${object.eq}`);
        }
        if (attributeIsSet(object.starts_with)) {
            conditions.push(`starts: "${object.starts_with}"`);
        }
        if (attributeIsSet(object.ends_with)) {
            conditions.push(`ends: "${object.ends_with}"`);
        }
        if (attributeIsSet(object.not_starts_with)) {
            conditions.push(`not starts: "${object.not_starts_with}"`);
        }
        if (attributeIsSet(object.not_ends_with)) {
            conditions.push(`not ends: "${object.not_ends_with}"`);
        }
        if (attributeIsSet(object.regex)) {
            conditions.push(`regex: /${object.regex}/`);
        }

        // Generate string for included values
        if (object.include_values && object.include_values.length > 0) {
            conditions.push(`incl: ${object.include_values.join(", ")}`);
        }

        // Generate string for excluded values
        if (object.exclude_values && object.exclude_values.length > 0) {
            conditions.push(`excl: ${object.exclude_values.join(", ")}`);
        }

        return <span><span className="me-3">{link}</span> {conditions.join(" | ")}</span>;
    };
    const getFieldForm = (object,) => {

        return [
            {
                label: "Meta location",
                type: "single-select",
                name: "meta_location",
                description: "Where to find the meta data. e.g. layer_meta, check_meta or fruit_meta",
                options: [
                    { label: "Layer meta", value: "layer_meta" },
                    { label: "Check meta", value: "check_meta" },
                    { label: "Fruit meta", value: "fruit_meta" },
                ]
            },
            {
                label: "Field",
                type: "text",
                description: "The meta field name to look for. e.g. sup_ref, country, etc.",
                name: "field",
            },
            {
                type: "header",
                label: "Numeric/String comparisons",
            },
            {
                label: "Options name",
                type: "text",
                description: "The field name to look for managed options. Most of the times will be the same as field.",
                name: "options_name",
            },
            {
                label: "Greater than or equal to",
                name: "gte",
                type: "number",
                description: "Filter when field value >= this value",
            },
            {
                label: "Less than",
                name: "lt",
                type: "number",
                description: "Filter when field value < this value",
            },
            {
                label: "Greater than",
                name: "gt",
                type: "number",
                description: "Filter when field value > this value",
            },
            {
                label: "Less than or equal to",
                name: "lte",
                type: "number",
                description: "Filter when field value <= this value",
            },
            {
                label: "Equals",
                name: "eq",
                type: "text",
                description: "Filter when field value equals this (string comparison)",
            },
            {
                type: "header",
                label: "Substring matching",
            },
            {
                label: "Starts with",
                name: "starts_with",
                type: "text",
                description: "Filter when field value starts with this string (e.g. Product_A for Product_A1, Product_A2)",
            },
            {
                label: "Ends with",
                name: "ends_with",
                type: "text",
                description: "Filter when field value ends with this string",
            },
            {
                label: "Does not start with",
                name: "not_starts_with",
                type: "text",
                description: "Filter when field value does not start with this string. Also passes when the field is empty",
            },
            {
                label: "Does not end with",
                name: "not_ends_with",
                type: "text",
                description: "Filter when field value does not end with this string. Also passes when the field is empty",
            },
            {
                label: "Regex",
                name: "regex",
                type: "text",
                description: "Keep it simple, mostly useful for OR statements, e.g. ^(RR|QAS)- or ^Product_(A|B)\\d+$. Matches anywhere in the value (case sensitive), use ^ and $ to anchor. Only basic syntax works in both backend and browser. Not supported: more than 5 quantifiers (* + ? {}), repeating a group, named groups, lookbehind, flags and backreferences. Invalid regexes never match.",
            },
            {
                type: "header",
                label: "Exact value matching",
            },
            {
                label: "Include values",
                type: "select-managed",
                options_name: object.options_name || object.field,
                name: "include_values",
                return_array: true,
                multi: true,
                description: "Only include items where field value is in this list",
            },
            {
                label: "Exclude values",
                type: "select-managed",
                options_name: object.options_name || object.field,
                name: "exclude_values",
                return_array: true,
                multi: true,
                description: "Exclude items where field value is in this list",
            },

        ];

    };
    const regexErrors = (fields as any[])
        .map((item) => (attributeIsSet(item?.field?.regex ?? item?.regex) ? validatePortableRegex(item?.field?.regex ?? item?.regex) : null))
        .filter(Boolean);
    return <FilterableConfigList
        name={name}
        title={title}
        description={description}
        error={regexErrors.length > 0 && `Invalid regex (this condition never matches): ${regexErrors.join("; ")}`}
        fields={fields}
        setArray={setArray}
        defaultField={defaultField}
        getFieldForm={getFieldForm}
        getFieldTitle={getFieldTitle}
        useDisplayFilter={false}
    />;

}


